/**
 * Cart Child Items Manager
 * Handles removal of child items (prints/artwork) when parent is removed
 * Also cleans up orphaned child items on cart load
 */

class CartChildItemsManager {
  constructor() {
    this.init();
  }

  init() {
    // Clean up orphaned items on page load
    this.cleanupOrphanedItems();

    // Listen for cart updates to clean up orphaned items
    document.addEventListener('cart:updated', () => {
      this.cleanupOrphanedItems();
    });

    // Intercept remove item clicks to also remove children
    this.interceptRemoveButtons();

    // Sync quantities
    this.interceptQuantityChanges();
  }

  interceptQuantityChanges() {
    this.debouncedHandleQuantitySync = FoxTheme.utils.debounce((input, event) => {
      this.handleQuantitySync(input, event);
    }, 300);

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.classList.contains('quantity__input') && target.closest('quantity-input')) {
        // Find if it has children first
        const cartItem = target.closest('.cart-item') || target.closest('tr.cart-item');
        if (cartItem && cartItem.dataset.customId) {
          event.stopImmediatePropagation();
          this.debouncedHandleQuantitySync(target, event);
        }
      }
    }, true);
  }

  async handleQuantitySync(input, event) {
    const cartItem = input.closest('.cart-item') || input.closest('tr.cart-item');
    if (!cartItem) return;

    const customId = cartItem.dataset.customId;
    if (!customId) return;

    const newQuantity = parseInt(input.value);
    if (isNaN(newQuantity)) return;

    // Show loaders
    this.showLoaders(input);

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('is-loading');

    const cartEl = document.querySelector('.cart');
    if (cartEl) cartEl.classList.add('is-loading');

    // Get cart to find items and keys
    const cart = await this.getCart();
    if (!cart) {
      this.hideLoaders(input);
      return;
    }

    const updates = {};
    let hasUpdates = false;

    cart.items.forEach((item) => {
      // If it's a child of this parent
      if (item.properties && item.properties._package === customId) {
        // Only sync if it's NOT an artwork (artwork has _image property)
        // Sync prints (_print_key) and services (_service_key)
        if (!item.properties._image) {
          if (item.quantity !== newQuantity) {
            updates[item.key] = newQuantity;
            hasUpdates = true;
          }
        }
      }
    });

    if (hasUpdates) {
      const parentIndex = input.dataset.index;
      const parentItem = cart.items[parseInt(parentIndex) - 1];
      if (parentItem) {
        updates[parentItem.key] = newQuantity;
      }

      await this.updateQuantities(updates, input);
    } else {
      // If no children need updating, we still need to update the parent if it's the one that triggered the sync
      const parentIndex = input.dataset.index;
      const parentItem = cart.items[parseInt(parentIndex) - 1];
      if (parentItem) {
        updates[parentItem.key] = newQuantity;
        await this.updateQuantities(updates, input);
      } else {
        this.hideLoaders(input);
      }
    }
  }

  showLoaders(input) {
    const parentIndex = input.dataset.index;
    if (!parentIndex) return;
    
    // Find all loaders for this line index (there might be multiple for mobile/desktop)
    const loaders = document.querySelectorAll(`[id$="-${parentIndex}"].btn-remove`);
    loaders.forEach(loader => loader.classList.add('btn--loading'));
  }

  hideLoaders(input) {
    const parentIndex = input.dataset.index;
    if (!parentIndex) return;
    
    const loaders = document.querySelectorAll(`[id$="-${parentIndex}"].btn-remove`);
    loaders.forEach(loader => loader.classList.remove('btn--loading'));
  }

  async updateQuantities(updates, input) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Failed to update quantities');
      }

      const parsedState = await response.json();
      
      // Notify other scripts that cart was updated
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: parsedState }
      }));

      // Trigger cart refresh to update UI
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { open: false }
      }));

    } catch (error) {
      console.error('Error updating quantities:', error);
      this.hideLoaders(input);
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.classList.remove('is-loading');

      const cartEl = document.querySelector('.cart');
      if (cartEl) cartEl.classList.remove('is-loading');
    }
  }

  interceptRemoveButtons() {
    // Use event delegation for dynamically added remove buttons
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[is="cart-remove-item"]');
      if (removeButton) {
        event.preventDefault();
        event.stopPropagation();
        this.handleRemoveWithChildren(removeButton);
      }
    }, true); // Use capture phase to intercept before other handlers
  }

  async handleRemoveWithChildren(removeButton) {
    const cartItem = removeButton.closest('.cart-item');
    if (!cartItem) return;

    const customId = cartItem.dataset.customId;
    const parentIndex = removeButton.dataset.index;

    // Show loader
    removeButton.classList.add('btn--loading');
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('is-loading');

    const cartEl = document.querySelector('.cart');
    if (cartEl) cartEl.classList.add('is-loading');

    // Get cart to find item keys
    const cart = await this.getCart();
    if (!cart) return;

    // Find parent item
    const parentItem = cart.items[parseInt(parentIndex) - 1];
    if (!parentItem) {
      console.error('Parent item not found');
      return;
    }

    // Get all items to remove (parent + children)
    const itemsToRemove = [this.getItemKey(parentItem)];

    if (customId) {
      // Find child items with matching package ID
      const childKeys = this.findChildItemKeys(cart, customId);
      itemsToRemove.push(...childKeys);
    }

    // Remove all items
    await this.removeMultipleItems(itemsToRemove);
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  getItemKey(item) {
    // Generate cart key in format: variant_id:properties_hash
    return item.key;
  }

  findChildItemKeys(cart, customId) {
    const childKeys = [];
    cart.items.forEach((item) => {
      if (item.properties && item.properties._package === customId) {
        childKeys.push(this.getItemKey(item));
      }
    });
    return childKeys;
  }

  async removeMultipleItems(itemKeys) {
    try {
      // Build updates object to set all items to quantity 0
      // Format: { "variant_id:properties_hash": 0 }
      const updates = {};
      itemKeys.forEach(key => {
        updates[key] = 0;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Failed to remove items');
      }

      // Trigger cart refresh
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { open: false }
      }));

    } catch (error) {
      console.error('Error removing items:', error);
      removeButton.classList.remove('btn--loading');
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.classList.remove('is-loading');

      const cartEl = document.querySelector('.cart');
      if (cartEl) cartEl.classList.remove('is-loading');

      alert('Failed to remove item. Please try again.');
    }
  }

  async cleanupOrphanedItems() {
    try {
      const cart = await this.getCart();
      if (!cart) return;

      // Find all parent custom IDs
      const parentCustomIds = new Set();
      cart.items.forEach(item => {
        if (item.properties && item.properties._custom) {
          parentCustomIds.add(item.properties._custom);
        }
      });

      // Find orphaned child items
      const orphanedKeys = [];
      cart.items.forEach((item) => {
        if (item.properties && item.properties._package) {
          const packageId = item.properties._package;
          if (!parentCustomIds.has(packageId)) {
            // This child's parent doesn't exist
            orphanedKeys.push(this.getItemKey(item));
          }
        }
      });

      // Remove orphaned items if any found
      if (orphanedKeys.length > 0) {
        console.log('Cleaning up orphaned cart items:', orphanedKeys);
        await this.removeMultipleItems(orphanedKeys);
      }

    } catch (error) {
      console.error('Error cleaning up orphaned items:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartChildItemsManager();
  });
} else {
  new CartChildItemsManager();
}
